import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { AxisSetExtremesEventObject } from 'highcharts/highstock';
import { isDatePickerValidDate } from '../DatePicker/tools/DatePicker.utils';
import { useAfterRenderEffect } from '../utils/useAfterRenderEffect';
import { DateTime } from 'luxon';
import React, { useCallback, useEffect, useState } from 'react';
import { MakeChangeRanges, QuotesChangeRange } from './chartsModel';

export enum ValidationMessageKeys {
  INVALID_DATE = 'invalid-date',
  INVALID_PERIOD = 'invalid-period',
  OUT_OF_PERIOD = 'out-of-period',
}

const isOutOfPeriodFunc = (date: MaterialUiPickersDate, lastDate: string): boolean => {
  return DateTime.fromJSDate(new Date(lastDate)).startOf('day') < DateTime.fromJSDate(date as never as Date).startOf('day');
};

const calculateInitialFromDate = (changeRange: string, changeRanges: QuotesChangeRange[]) => {
  const changeRangesFromDate = changeRange && changeRanges.find(e => e.key === changeRange);
  return changeRange && changeRangesFromDate ? changeRangesFromDate.fromDate : changeRanges[0].fromDate;
}

export const useChartDates = (
    makeChangeRanges: MakeChangeRanges, defaultChangeRangePosition?: number, changeRateKey?: string, lastFundDate?: string,
) => {
  const [changeRanges, setChangeRanges] = useState(makeChangeRanges());
  const changeRangePosition = defaultChangeRangePosition ?? 0;

  const changeRangeDefault = (changeRateKey && changeRanges.find(e => e.roiKey === changeRateKey)) || changeRanges[changeRangePosition];
  const [changeRange, setChangeRange] = useState<QuotesChangeRange | undefined>(changeRangeDefault);

  const changeRangeKey = changeRange?.key || '';

  const initialFromDate = calculateInitialFromDate(changeRangeKey, changeRanges);
  const [fromDate, setFromDate] = useState(initialFromDate);
  const [endDate, setEndDate] = useState<string>(lastFundDate || DateTime.local().toISODate());
  const [lastDate, setLastDate] = useState(lastFundDate || DateTime.local().toISODate());
  const [errorMessage, setErrorMessage] = useState({ fromDate: '', endDate: '' });
  const [isReadyForChartApiCall, setIsReadyForChartApiCall] = useState(true);

  useEffect(() => {
    setChangeRanges(() => {
      const newChangeRanges = makeChangeRanges();
      const newChangeRange = (changeRateKey && newChangeRanges.find(e => e.roiKey === changeRateKey)) ||
          newChangeRanges[changeRangePosition];
      setChangeRange(newChangeRange);

      const newInitialFromDate = calculateInitialFromDate(newChangeRange.key, newChangeRanges);
      setFromDate(newInitialFromDate);
      return newChangeRanges;
    });
  }, [makeChangeRanges, changeRangePosition, changeRateKey]);

  useEffect(() => {
    if (lastFundDate) {
      const newRanges = makeChangeRanges(DateTime.fromISO(lastFundDate));
      setChangeRanges(newRanges);
      setEndDate(lastFundDate);
      setLastDate(lastFundDate);

      const newRange = newRanges.find(e => e.key === changeRangeKey);

      if (newRange) {
        setFromDate(newRange.fromDate);
      }
    }
  }, [changeRangeKey, lastFundDate, makeChangeRanges]);

  useAfterRenderEffect(() => {
    if (!errorMessage.endDate && !errorMessage.fromDate) {
      setIsReadyForChartApiCall(true);
    }
  }, [errorMessage.endDate, errorMessage.fromDate]);

  const clearErrors = () => {
    setErrorMessage({ fromDate: '', endDate: '' });
  };

  const handleFromDateChange = (newDate: MaterialUiPickersDate) => {
    const isIvalidPeriod = endDate != null && (DateTime.fromJSDate(new Date(endDate)).startOf('day') <= DateTime.fromJSDate(newDate as never as Date).startOf('day'));
    const isOutOfPeriod = isOutOfPeriodFunc(newDate, lastDate);

    setFromDate(newDate ? DateTime.fromJSDate(newDate).toISODate() : initialFromDate);
    setErrorMessage(prevState => ({
      fromDate: '',
      endDate: prevState.endDate !== ValidationMessageKeys.INVALID_PERIOD ? prevState.endDate : ''
    }));

    if (!isDatePickerValidDate(newDate)) {
      setErrorMessage(prevState => ({ ...prevState, fromDate: ValidationMessageKeys.INVALID_DATE }));
      setIsReadyForChartApiCall(false);
    } else if (isOutOfPeriod) {
      setErrorMessage(prevState => ({ ...prevState, fromDate: ValidationMessageKeys.OUT_OF_PERIOD }));
      setIsReadyForChartApiCall(false);
    } else if (isIvalidPeriod) {
      setErrorMessage(prevState => ({
        fromDate: ValidationMessageKeys.INVALID_PERIOD,
        endDate: prevState.endDate === ValidationMessageKeys.INVALID_PERIOD ? '' : prevState.endDate
      }));
      setIsReadyForChartApiCall(false);
    } else if (!errorMessage.endDate && !errorMessage.fromDate) {
      setIsReadyForChartApiCall(true);
      setChangeRange(undefined);
    }
  };

  const handleEndDateChange = (newDate: MaterialUiPickersDate) => {
    const isIvalidPeriod = fromDate != null && (DateTime.fromJSDate(new Date(fromDate)).startOf('day') >= DateTime.fromJSDate(newDate as never as Date).startOf('day'));
    const isOutOfPeriod = isOutOfPeriodFunc(newDate, lastDate);

    setEndDate(newDate ? DateTime.fromJSDate(newDate).toISODate() : DateTime.local().toISODate());
    setErrorMessage(prevState => ({
      fromDate: prevState.fromDate !== ValidationMessageKeys.INVALID_PERIOD ? prevState.fromDate : '',
      endDate: ''
    }));

    if (!isDatePickerValidDate(newDate)) {
      setErrorMessage(prevState => ({ ...prevState, endDate: ValidationMessageKeys.INVALID_DATE }));
      setIsReadyForChartApiCall(false);
    } else if (isOutOfPeriod) {
      setErrorMessage(prevState => ({ ...prevState, endDate: ValidationMessageKeys.OUT_OF_PERIOD }));
      setIsReadyForChartApiCall(false);
    } else if (isIvalidPeriod) {
      setErrorMessage(prevState => ({
        fromDate: prevState.fromDate === ValidationMessageKeys.INVALID_PERIOD ? '' : prevState.fromDate,
        endDate: ValidationMessageKeys.INVALID_PERIOD
      }));
      setIsReadyForChartApiCall(false);
    } else if (!errorMessage.fromDate && !errorMessage.endDate ) {
      setIsReadyForChartApiCall(true);
      setChangeRange(undefined);
    }
  };

  const handleDateRangeChange = (event: React.ChangeEvent<{ name?: string; value: unknown; }>) => {
    clearErrors();
    setIsReadyForChartApiCall(true);
    const newChangeRange = changeRanges.find(obj => obj.key === event.target.value);
    newChangeRange && setChangeRange(newChangeRange);
    newChangeRange && setFromDate(newChangeRange.fromDate);
    setEndDate(lastDate);
  };

  const handleNavigatorChange = useCallback((event: AxisSetExtremesEventObject) => {
    const min = typeof event.min === 'number' ? event.min : undefined;
    const max = typeof event.max === 'number' ? event.max : undefined;

    setIsReadyForChartApiCall(false);
    if (min !== undefined) {
      setFromDate(DateTime.fromMillis(min).toISODate());
    }
    if (max !== undefined) {
      setEndDate(DateTime.fromMillis(max).toISODate());
    }
    setChangeRange(undefined);
  }, []);

  return {
    fromDate,
    endDate,
    lastDate,
    changeRange,
    changeRanges,
    handleNavigatorChange,
    handleFromDateChange,
    handleEndDateChange,
    handleDateRangeChange,
    setFromDate,
    setEndDate,
    setIsReadyForChartApiCall,
    isReadyForChartApiCall,
    errorMessage,
  };
};